import DSPDeliveryModal from "./DSPDeliveryModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ConfirmationModal from "./ConfirmationModal";
import DraftPitchReminderModal from "./DraftPitchReminderModal";
import AddContributorModal from "../../PitchFormPage/components/contributors/AddContributorModal";
import CreateUpdateModal from "./CreateUpdateModal";
import UserAppUpdatesModal from "./UserAppUpdatesModal";
import ViewPitchDspModal from "./ViewPitchDspModal";
import SetReminderModal from "./SetReminderModal";
import DueRemindersModal from "./DueRemindersModal";
import { Contributor } from "../../PitchFormPage/types/form";
import { DueReminder, Reminder } from "../../types/reminder";

export type ModalType = "DSP_DELIVERY" | "DELETE_CONFIRMATION" | "CONFIRMATION" | "ADD_CONTRIBUTOR" | "DRAFT_PITCH_REMINDER" | "CREATE_UPDATE" | "USER_APP_UPDATES" | "VIEW_PITCH_DSP" | "SET_REMINDER" | "DUE_REMINDERS";

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// DSP Delivery Modal Props
interface DSPDeliveryProps extends BaseModalProps {
  type: "DSP_DELIVERY";
  pitchId?: number;
  initialDSPs?: string[];
  mode?: "admin" | "user";
  onSubmit: (selectedDSPs: string[]) => void;
}

// Delete Confirmation Modal Props
interface DeleteConfirmationProps extends BaseModalProps {
  type: "DELETE_CONFIRMATION";
  inputValue: string;
  setInputValue: (value: string) => void;
  onConfirm: () => void;
  loading?: boolean;
}

// Confirmation Modal Props
interface ConfirmationProps extends BaseModalProps {
  type: "CONFIRMATION";
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  isDestructive?: boolean;
}

// Add Contributor Modal Props
interface AddContributorProps extends BaseModalProps {
  type: "ADD_CONTRIBUTOR";
  contributor?: Contributor | null;
  mode?: "add" | "edit";
  onSave: (contributor: { name: string; roles: string; id?: string | number }) => void;
}

// Draft Pitch Reminder Modal Props
interface DraftPitchReminderProps {
  type: "DRAFT_PITCH_REMINDER";
  isOpen: boolean;
  userName: string;
  draftPitches: Array<{
    id: number;
    artistName: string;
    releaseTitle: string;
    releaseDate: string;
  }>;
  onViewDrafts: () => void;
  onRemindLater: () => void;
  onDismiss: () => void;
}

// Create Update Modal Props
interface CreateUpdateProps extends BaseModalProps {
  type: "CREATE_UPDATE";
  onDraft: (title: string, content: string) => void;
  onPublish: (title: string, content: string) => void;
  loading?: boolean;
  mode?: "create" | "edit";
  initialContent?: string;
  initialTitle?: string;
  updateId?: number;
  currentStatus?: "published" | "draft";
}

// User App Updates Modal Props
interface UserAppUpdatesProps extends BaseModalProps {
  type: "USER_APP_UPDATES";
  onDismiss: (updateId: number) => void;
  userId: number;
  userRole: string;
}

// View Pitch DSP Modal Props
interface ViewPitchDspProps extends BaseModalProps {
  type: "VIEW_PITCH_DSP";
  pitch: {
    id: number;
    userId: number;
    artistId: number | null;
    artistName: string;
    releaseTitle: string;
    releaseDate: string;
    configuration: string;
    releaseUPC: string;
    focusTrackName: string;
    trackISRC: string;
    featuredArtists: string;
    featuredArtistURI: string;
    altTitles: string;
    shortDescription: string;
    longDescription: string;
    lyrics: string;
    songDescription: string;
    mainGenre: string;
    songMood: string;
    language: string;
    linkToListen: string;
    promoPhotos: string;
    images: any[] | null;
    status: string;
    companyName: string;
    createdAt: string;
    updatedAt: string;
    AmazonInfo: any;
    SpotifyInfo: any;
    DeezerInfo: any;
    TidalInfo: any;
    AudiomackInfo: any;
    SoundCloudInfo: any;
    AppleInfo: any;
    YoutubeInfo: any;
    SiriusXMInfo: any;
    contributors: any[];
    starred: boolean;
  };
}

// Set Reminder Modal Props
interface SetReminderProps extends BaseModalProps {
  type: "SET_REMINDER";
  pitchId: number;
  artistName?: string;
  releaseTitle?: string;
  existingReminder?: Reminder;
  onSuccess?: () => void;
}

// Due Reminders Modal Props
interface DueRemindersProps extends BaseModalProps {
  type: "DUE_REMINDERS";
  reminders: DueReminder[];
  onDismiss: (id: number) => void;
  onGoToPitch: (pitchId: number, id: number) => void;
  onEdit: (reminder: DueReminder) => void;
  onDelete: (id: number) => void;
}

export type ModalRendererProps = DSPDeliveryProps | DeleteConfirmationProps | ConfirmationProps | AddContributorProps | DraftPitchReminderProps | CreateUpdateProps | UserAppUpdatesProps | ViewPitchDspProps | SetReminderProps | DueRemindersProps;

const ModalRenderer: React.FC<ModalRendererProps> = (props) => {
  const { type, isOpen } = props;

  switch (type) {
    case "DSP_DELIVERY":
      return (
        <DSPDeliveryModal
          isOpen={isOpen}
          onClose={props.onClose}
          onSubmit={props.onSubmit}
          initialDSPs={props.initialDSPs || []}
          mode={props.mode || "admin"}
          pitchId={props.pitchId}
        />
      );

    case "DELETE_CONFIRMATION":
      return (
        <DeleteConfirmationModal
          open={isOpen}
          onClose={props.onClose}
          onConfirm={props.onConfirm}
          inputValue={props.inputValue}
          setInputValue={props.setInputValue}
          loading={props.loading}
        />
      );

    case "CONFIRMATION":
      return (
        <ConfirmationModal
          isOpen={isOpen}
          onClose={props.onClose}
          onConfirm={props.onConfirm}
          title={props.title}
          message={props.message}
          confirmText={props.confirmText}
          cancelText={props.cancelText}
          isDestructive={props.isDestructive}
        />
      );

    case "ADD_CONTRIBUTOR":
      return (
        <AddContributorModal
          isOpen={isOpen}
          onClose={props.onClose}
          onSave={props.onSave}
          contributor={props.contributor}
          mode={props.mode}
        />
      );

    case "DRAFT_PITCH_REMINDER":
      return (
        <DraftPitchReminderModal
          isOpen={isOpen}
          userName={props.userName}
          draftPitches={props.draftPitches}
          onViewDrafts={props.onViewDrafts}
          onRemindLater={props.onRemindLater}
          onDismiss={props.onDismiss}
        />
      );

    case "CREATE_UPDATE":
      return (
        <CreateUpdateModal
          isOpen={isOpen}
          onClose={props.onClose}
          onDraft={props.onDraft}
          onPublish={props.onPublish}
          loading={props.loading}
          mode={props.mode}
          initialContent={props.initialContent}
          initialTitle={props.initialTitle}
          updateId={props.updateId}
          currentStatus={props.currentStatus}
        />
      );

    case "USER_APP_UPDATES":
      return (
        <UserAppUpdatesModal
          isOpen={isOpen}
          onClose={props.onClose}
          onDismiss={props.onDismiss}
          userId={props.userId}
          userRole={props.userRole}
        />
      );

    case "VIEW_PITCH_DSP":
      return (
        <ViewPitchDspModal
          isOpen={isOpen}
          onClose={props.onClose}
          pitch={props.pitch}
        />
      );

    case "SET_REMINDER":
      return (
        <SetReminderModal
          isOpen={isOpen}
          onClose={props.onClose}
          pitchId={props.pitchId}
          artistName={props.artistName}
          releaseTitle={props.releaseTitle}
          existingReminder={props.existingReminder}
          onSuccess={props.onSuccess}
        />
      );

    case "DUE_REMINDERS":
      return (
        <DueRemindersModal
          isOpen={isOpen}
          onClose={props.onClose}
          reminders={props.reminders}
          onDismiss={props.onDismiss}
          onGoToPitch={props.onGoToPitch}
          onEdit={props.onEdit}
          onDelete={props.onDelete}
        />
      );

    default:
      return null;
  }
};

export default ModalRenderer;
